import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useGoogleMaps } from '../hooks/useGoogleMaps';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import AddressAutocomplete from '../components/AddressAutocomplete';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Calendar as CalendarIcon, Clock, MapPin, Users, FileText, Phone } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { formatDateUK, formatDateISO, formatCurrencySimple } from '../utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// South African phone number validation and formatting
const validateSAPhoneNumber = (phone) => {
  // Remove all non-digit characters except +
  const cleaned = phone.replace(/[^\d+]/g, '');
  
  // Valid formats:
  // +27XXXXXXXXX (11 chars with +27)
  // 27XXXXXXXXX (11 digits starting with 27)
  // 0XXXXXXXXX (10 digits starting with 0)
  
  const patterns = [
    /^\+27[0-9]{9}$/,      // +27 followed by 9 digits
    /^27[0-9]{9}$/,        // 27 followed by 9 digits  
    /^0[0-9]{9}$/          // 0 followed by 9 digits
  ];
  
  return patterns.some(pattern => pattern.test(cleaned));
};

const formatSAPhoneNumber = (phone) => {
  // Remove all non-digit characters
  let cleaned = phone.replace(/[^\d]/g, '');
  
  // If starts with 27, add +
  if (cleaned.startsWith('27') && cleaned.length === 11) {
    return `+${cleaned}`;
  }
  
  // If starts with 0, convert to +27 format
  if (cleaned.startsWith('0') && cleaned.length === 10) {
    return `+27${cleaned.substring(1)}`;
  }
  
  // If already has +27 format
  if (phone.startsWith('+27')) {
    return phone.replace(/[^\d+]/g, '');
  }
  
  return phone;
};

const BookingForm = () => {
  const { systemId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { isLoaded: mapsLoaded, loadError } = useGoogleMaps();
  
  const [system, setSystem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  
  const [formData, setFormData] = useState({
    eventName: '',
    eventType: '',
    eventDate: location.state?.selectedDate || null,
    eventTime: '',
    duration: '',
    location: '',
    attendees: '',
    contactPhone: '',
    specialRequirements: ''
  });

  const eventTypes = ['Wedding', 'Conference', 'Concert', 'Party', 'Corporate Event', 'Meeting', 'Festival', 'Other'];
  const durations = ['2 hours', '4 hours', '6 hours', '8 hours', '12 hours', '1 day', '2 days', '3 days'];

  const fetchSystem = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/systems/${systemId}`);
      setSystem(response.data);
    } catch (error) {
      console.error('Failed to fetch system:', error);
      toast({
        title: "Error",
        description: "Failed to load PA system details",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [systemId]);

  useEffect(() => {
    fetchSystem();
  }, [fetchSystem]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // Validate phone number
      if (!validateSAPhoneNumber(formData.contactPhone)) {
        toast({
          title: "Invalid phone number",
          description: "Please enter a valid South African phone number",
          variant: "destructive"
        });
        setSubmitting(false);
        return;
      }

      // Format phone number before submission
      const formattedPhone = formatSAPhoneNumber(formData.contactPhone);

      const bookingData = {
        system_id: systemId,
        business_id: system.business_id,
        event_name: formData.eventName,
        event_type: formData.eventType,
        event_date: formatDateISO(formData.eventDate),
        event_time: formData.eventTime,
        duration: formData.duration,
        location: formData.location,
        attendees: parseInt(formData.attendees),
        contact_phone: formattedPhone,
        special_requirements: formData.specialRequirements || null
      };

      const response = await axios.post(`${API}/bookings`, bookingData, {
        withCredentials: true
      });

      // Navigate to payment
      navigate('/payment', { state: { booking: response.data, system } });
    } catch (error) {
      console.error('Booking failed:', error);
      // Handle Pydantic validation errors (array of objects) or simple error messages
      let errorMessage = "Failed to create booking. Please try again.";
      const detail = error.response?.data?.detail;
      if (detail) {
        if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg || err).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        }
      }
      toast({
        title: "Booking Failed",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const isFormValid = formData.eventName && formData.eventType && formData.eventDate && 
                      formData.eventTime && formData.duration && formData.location && 
                      formData.attendees && formData.contactPhone && 
                      validateSAPhoneNumber(formData.contactPhone) && !phoneError;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500" />
      </div>
    );
  }

  if (!system) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">System Not Found</h2>
          <Button onClick={() => navigate('/browse')} className="bg-yellow-500 hover:bg-yellow-600 text-black">Back to Browse</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Button
          variant="ghost"
          className="mb-6 text-gray-400 hover:text-white hover:bg-gray-800"
          onClick={() => navigate(`/system/${systemId}`)}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to System Details
        </Button>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Form */}
          <div className="lg:col-span-2">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-3xl text-white">Book Your PA System</CardTitle>
                <CardDescription className="text-gray-400">
                  Fill in your event details to complete the booking
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="booking-form">
                  <div className="space-y-2">
                    <Label htmlFor="eventName" className="text-gray-300">
                      Event Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="eventName"
                      data-testid="event-name-input"
                      placeholder="e.g., Annual Corporate Conference"
                      value={formData.eventName}
                      onChange={(e) => handleChange('eventName', e.target.value)}
                      required
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="eventType" className="text-gray-300">
                      Event Type <span className="text-red-500">*</span>
                    </Label>
                    <Select value={formData.eventType} onValueChange={(value) => handleChange('eventType', value)}>
                      <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                        <SelectValue placeholder="Select event type" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700">
                        {eventTypes.map(type => (
                          <SelectItem key={type} value={type} className="text-gray-300 hover:text-white">{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-gray-300">
                        Event Date <span className="text-red-500">*</span>
                      </Label>
                      <Calendar
                        mode="single"
                        selected={formData.eventDate}
                        onSelect={(date) => handleChange('eventDate', date)}
                        disabled={(date) => date < new Date()}
                        className="rounded-md w-full text-white"
                      />
                    </div>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="eventTime" className="text-gray-300">
                          Start Time <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="eventTime"
                          type="time"
                          value={formData.eventTime}
                          onChange={(e) => handleChange('eventTime', e.target.value)}
                          required
                          className="bg-gray-700 border-gray-600 text-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="duration" className="text-gray-300">
                          Duration <span className="text-red-500">*</span>
                        </Label>
                        <Select value={formData.duration} onValueChange={(value) => handleChange('duration', value)}>
                          <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                            <SelectValue placeholder="Select duration" />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-800 border-gray-700">
                            {durations.map(duration => (
                              <SelectItem key={duration} value={duration} className="text-gray-300 hover:text-white">{duration}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Location with Google Maps Autocomplete */}
                  <div className="space-y-2">
                    {mapsLoaded ? (
                      <AddressAutocomplete
                        label="Event Location"
                        value={formData.location}
                        onChange={(value) => handleChange('location', value)}
                        placeholder="Start typing your event address..."
                        required
                      />
                    ) : (
                      <>
                        <Label htmlFor="location" className="text-gray-300">
                          Event Location <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="location"
                          placeholder="Full address of the event venue"
                          value={formData.location}
                          onChange={(e) => handleChange('location', e.target.value)}
                          required
                          className="bg-gray-700 border-gray-600 text-white"
                        />
                        {loadError && (
                          <p className="text-xs text-orange-500">
                            Address autocomplete unavailable. Please enter address manually.
                          </p>
                        )}
                      </>
                    )}
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="attendees" className="text-gray-300">
                        Expected Attendees <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="attendees"
                        type="number"
                        placeholder="Number of people"
                        value={formData.attendees}
                        onChange={(e) => handleChange('attendees', e.target.value)}
                        required
                        className="bg-gray-700 border-gray-600 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contactPhone" className="text-gray-300 flex items-center gap-2">
                        <Phone className="h-4 w-4" />
                        Contact Phone <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="contactPhone"
                        type="tel"
                        placeholder="(+27) 065 910 4999"
                        value={formData.contactPhone}
                        onChange={(e) => {
                          const value = e.target.value;
                          handleChange('contactPhone', value);
                          // Validate on change
                          if (value && !validateSAPhoneNumber(value)) {
                            setPhoneError('Please enter a valid SA number: (+27) 0XX XXX XXXX');
                          } else {
                            setPhoneError('');
                          }
                        }}
                        onBlur={(e) => {
                          // Format on blur if valid
                          const value = e.target.value;
                          if (value && validateSAPhoneNumber(value)) {
                            handleChange('contactPhone', formatSAPhoneNumber(value));
                          }
                        }}
                        required
                        className={`bg-gray-700 border-gray-600 text-white ${phoneError ? 'border-red-500' : ''}`}
                      />
                      {phoneError && (
                        <p className="text-red-400 text-xs mt-1">{phoneError}</p>
                      )}
                      <p className="text-gray-500 text-xs">Format: (+27) 0XX XXX XXXX or 0XX XXX XXXX</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="specialRequirements" className="text-gray-300">
                      Special Requirements (Optional)
                    </Label>
                    <Textarea
                      id="specialRequirements"
                      placeholder="Any special requests or requirements for your event..."
                      rows={4}
                      value={formData.specialRequirements}
                      onChange={(e) => handleChange('specialRequirements', e.target.value)}
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>

                  {/* Cancellation Policy Notice */}
                  <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
                    <p className="text-orange-400 text-sm font-medium mb-1">⚠️ Cancellation Policy</p>
                    <p className="text-orange-300/80 text-xs">
                      A <strong>20% cancellation fee</strong> applies to all paid bookings that are cancelled. 
                      Refunds (minus the fee) are processed within 5-7 business days.
                    </p>
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold text-lg py-6 transition-all"
                    disabled={!isFormValid || submitting}
                  >
                    {submitting ? 'Creating Booking...' : 'Continue to Payment'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Summary */}
          <div>
            <Card className="sticky top-24 bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Booking Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <img
                    src={system.image}
                    alt={system.name}
                    className="w-full h-32 object-cover rounded-lg mb-3"
                  />
                  <h3 className="font-bold text-lg text-white">{system.name}</h3>
                  <p className="text-sm text-gray-400">{system.category}</p>
                </div>

                <div className="space-y-3 text-sm">
                  {formData.eventName && (
                    <div className="flex items-start">
                      <FileText className="h-4 w-4 mr-2 mt-0.5 text-yellow-500" />
                      <div>
                        <p className="text-gray-500">Event</p>
                        <p className="font-medium text-white">{formData.eventName}</p>
                      </div>
                    </div>
                  )}
                  {formData.eventDate && (
                    <div className="flex items-start">
                      <CalendarIcon className="h-4 w-4 mr-2 mt-0.5 text-yellow-500" />
                      <div>
                        <p className="text-gray-500">Date</p>
                        <p className="font-medium text-white">{formatDateUK(formData.eventDate)}</p>
                      </div>
                    </div>
                  )}
                  {formData.eventTime && (
                    <div className="flex items-start">
                      <Clock className="h-4 w-4 mr-2 mt-0.5 text-yellow-500" />
                      <div>
                        <p className="text-gray-500">Time</p>
                        <p className="font-medium text-white">{formData.eventTime}</p>
                      </div>
                    </div>
                  )}
                  {formData.location && (
                    <div className="flex items-start">
                      <MapPin className="h-4 w-4 mr-2 mt-0.5 text-yellow-500" />
                      <div>
                        <p className="text-gray-500">Location</p>
                        <p className="font-medium text-white">{formData.location}</p>
                      </div>
                    </div>
                  )}
                  {formData.attendees && (
                    <div className="flex items-start">
                      <Users className="h-4 w-4 mr-2 mt-0.5 text-yellow-500" />
                      <div>
                        <p className="text-gray-500">Attendees</p>
                        <p className="font-medium text-white">{formData.attendees} people</p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="pt-4 border-t border-gray-700">
                  <div className="flex justify-between items-baseline">
                    <span className="text-gray-400">Total Amount</span>
                    <div className="text-right">
                      <span className="text-2xl font-bold text-yellow-500">{formatCurrencySimple(system.price)}</span>
                      <p className="text-xs text-gray-500">{system.price_unit}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingForm;
